// Standalone signing of an externally produced document — /sign?t=<link token>
//
// A clinician (not a patient) reviews a finished PDF another system produced —
// first user: the booking backoffice's DRV forms — and signs it. Depending on
// the request the signer confirms with a code sent by e-mail or SMS, or without
// a code. The signer's stored signature picture, when supplied, is shown before
// signing: Youtrust places exactly that picture on the document.
//
// Independent of the questionnaire and of informed consent: own session
// (X-Signing-Token). The link can be re-opened until the document is signed;
// afterwards it only shows the signed result.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CheckCircle2, ShieldCheck, FileText, ExternalLink, Download, AlertTriangle, Loader2 } from 'lucide-react'
import { Logo } from '../components/Logo'
import { Button } from '../components/Button'
import { useTenant } from '../context/TenantContext'
import { signingService, SigningRequestSummary, getApiErrorCode, getApiErrorMessage, getApiErrorDetails } from '../services/api'
import { logger } from '../utils/logger'
import { LegalFooter } from '../components/LegalFooter'

type Phase = 'loading' | 'invalid' | 'expired' | 'canceled' | 'read' | 'code' | 'signed'

const OTP_LENGTH = 6
const RESEND_COOLDOWN_S = 30

export function SignDocumentPage() {
  const { t, i18n } = useTranslation()
  // The exchange must carry X-Tenant-ID (resolved from the hostname): wait for the tenant.
  const { tenant, loading: tenantLoading } = useTenant()

  const [phase, setPhase] = useState<Phase>('loading')
  const [session, setSession] = useState<string | null>(null)
  const [request, setRequest] = useState<SigningRequestSummary | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null)
  // true once the blob in pdfUrl is the SIGNED copy (not the pre-signature document)
  const [signedPdfReady, setSignedPdfReady] = useState(false)
  const [copyFollowsLater, setCopyFollowsLater] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [code, setCode] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const [destination, setDestination] = useState('')
  const [cooldown, setCooldown] = useState(0)
  const otpRefs = useRef<(HTMLInputElement | null)[]>([])

  const linkToken = new URLSearchParams(window.location.search).get('t') || ''
  // Mobile browsers do not render PDFs inside an iframe reliably — offer a button instead.
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)

  const loadPdf = useCallback(async (sess: string) => {
    setPdfLoading(true)
    try {
      const blob = await signingService.document(sess)
      // Revoke the previous blob only after a grace period: a download or a new tab
      // started a moment earlier must not end up with an empty file.
      setPdfUrl(prev => { if (prev) setTimeout(() => URL.revokeObjectURL(prev), 60_000); return URL.createObjectURL(blob) })
      return true
    } catch (err) {
      logger.warn('[Signing] document not available yet:', getApiErrorCode(err))
      return false
    } finally {
      setPdfLoading(false)
    }
  }, [])

  // Exchange the link for a session
  useEffect(() => {
    if (tenantLoading) return
    if (!linkToken) { setPhase('invalid'); return }
    let cancelled = false
    ;(async () => {
      try {
        const { sessionToken, request: req } = await signingService.exchange(linkToken)
        if (cancelled) return
        if (req.locale && req.locale !== i18n.language) i18n.changeLanguage(req.locale)
        setSession(sessionToken)
        setRequest(req)
        const signed = ['signed', 'stored'].includes(req.status)
        setPhase(signed ? 'signed' : 'read')
        loadPdf(sessionToken).then(ok => { if (ok && req.stored) setSignedPdfReady(true) })
        if (!signed && req.hasSignatureImage) {
          signingService.signatureImage(sessionToken)
            .then(blob => { if (!cancelled) setSignatureUrl(URL.createObjectURL(blob)) })
            .catch(err => logger.warn('[Signing] signature picture not available:', getApiErrorCode(err)))
        }
      } catch (err) {
        if (cancelled) return
        const c = getApiErrorCode(err)
        // Explain it in the language the link was sent in
        const linkLocale = getApiErrorDetails(err)?.locale
        if (typeof linkLocale === 'string' && linkLocale !== i18n.language) i18n.changeLanguage(linkLocale)
        if (c === 'signing_link_expired') setPhase('expired')
        else if (c === 'signing_canceled') setPhase('canceled')
        else setPhase('invalid')
      }
    })()
    return () => { cancelled = true }
  }, [tenantLoading, linkToken, loadPdf, i18n])

  // After signing: poll until the sealed copy is stored, then load it
  useEffect(() => {
    if (phase !== 'signed' || !session || request?.stored) return
    let tries = 0
    const id = setInterval(async () => {
      tries += 1
      try {
        const me = await signingService.me(session)
        setRequest(me)
        if (me.stored) { clearInterval(id); loadPdf(session).then(ok => { if (ok) setSignedPdfReady(true) }) }
      } catch { /* keep polling */ }
      if (tries >= 30) { clearInterval(id); setCopyFollowsLater(true) }
    }, 3000)
    return () => clearInterval(id)
  }, [phase, session, request?.stored, loadPdf])

  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown(c => c - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  /** Errors that change the page state or have one fixed message, for both "request code" and "sign". */
  const handleCommonError = (err: unknown): boolean => {
    const c = getApiErrorCode(err)
    if (c === 'signing_busy') {
      const retryAfter = Number((err as { response?: { headers?: Record<string, string> } })?.response?.headers?.['retry-after'])
      const minutes = Math.max(1, Math.ceil((Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 120) / 60))
      toast.error(t('signing.busy', { minutes }), { duration: 10000 })
      return true
    }
    if (c === 'signing_session_expired' || c === 'signing_session_required') {
      // The session ran out while reading — the link is still valid, just reload it
      window.location.reload()
      return true
    }
    if (c === 'signing_link_expired') { setPhase('expired'); return true }
    if (c === 'signing_canceled') { setPhase('canceled'); return true }
    if (c === 'already_signed') { setPhase('signed'); return true }
    if (c === 'sr_cap_reached') { toast.error(t('signing.capReached'), { duration: 12000 }); return true }
    if (c === 'phone_invalid') { toast.error(t('signing.phoneInvalid'), { duration: 12000 }); return true }
    if (c === 'sr_create_failed' || c === 'yousign_not_configured' || c === 'document_not_ready') { toast.error(t('signing.prepareFailed')); return true }
    if (c === 'signing_in_progress') { toast.error(t('signing.inProgress')); return true }
    return false
  }

  const sendCode = async (resend = false) => {
    if (!session || !agreed) return
    setBusy(true)
    try {
      const r = await signingService.sendCode(session)
      setDestination(r.destinationMasked || '')
      setCooldown(RESEND_COOLDOWN_S)
      setCode(Array(OTP_LENGTH).fill(''))
      setPhase('code')
      toast.success(resend ? t('signing.newCodeSent') : t('signing.codeSent'))
      setTimeout(() => otpRefs.current[0]?.focus(), 50)
    } catch (err) {
      if (handleCommonError(err)) return
      if (getApiErrorCode(err) === 'otp_rate_limited') toast.error(t('common.tooManyOtpRequests'))
      else toast.error(getApiErrorMessage(err) || t('common.errorSendingCode'))
      logger.error('[Signing] send-code failed:', err)
    } finally {
      setBusy(false)
    }
  }

  const sign = async () => {
    if (!session || !request) return
    const needsCode = request.authentication !== 'none'
    const otp = code.join('')
    if (!agreed) { toast.error(t('signing.agreementRequired')); return }
    if (needsCode && otp.length !== OTP_LENGTH) return
    setBusy(true)
    try {
      await signingService.sign(session, needsCode ? otp : null)
      setPhase('signed')
      setRequest(r => r ? { ...r, status: 'signed', signedAt: new Date().toISOString() } : r)
      toast.success(t('signing.signedToast'))
    } catch (err) {
      if (handleCommonError(err)) return
      const c = getApiErrorCode(err)
      if (c === 'otp_invalid' || c === 'otp_rejected') toast.error(t('common.invalidCodeRetry'))
      else if (c === 'otp_expired') toast.error(t('signing.codeExpired'))
      else if (c === 'otp_rate_limited') toast.error(t('common.tooManyAttempts'))
      else if (c === 'code_not_sent') { toast.error(t('signing.codeNotSent')); setPhase('read') }
      else if (c === 'agreement_required') toast.error(t('signing.agreementRequired'))
      else if (c === 'ip_detection_failed') toast.error(t('signing.ipFailed'))
      else if (c === 'sign_rejected') toast.error(t('signing.signRejected'))
      else toast.error(getApiErrorMessage(err) || t('common.serverError'))
      logger.error('[Signing] sign failed:', c)
      if (needsCode) { setCode(Array(OTP_LENGTH).fill('')); otpRefs.current[0]?.focus() }
    } finally {
      setBusy(false)
    }
  }

  // Code input helpers (same UX as the questionnaire and the consent page)
  const onOtpChange = (i: number, v: string) => {
    const d = v.replace(/\D/g, '').slice(-1)
    setCode(prev => { const n = [...prev]; n[i] = d; return n })
    if (d && i < OTP_LENGTH - 1) otpRefs.current[i + 1]?.focus()
  }
  const onOtpKeyDown = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) otpRefs.current[i - 1]?.focus()
  }
  const onOtpPaste = (e: React.ClipboardEvent) => {
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH).split('')
    if (digits.length === 0) return
    e.preventDefault()
    setCode(Array.from({ length: OTP_LENGTH }, (_, i) => digits[i] || ''))
    otpRefs.current[Math.min(digits.length, OTP_LENGTH - 1)]?.focus()
  }

  const openPdf = () => { if (pdfUrl) window.open(`${pdfUrl}#navpanes=0&pagemode=none`, '_blank', 'noopener') }
  const download = () => {
    if (!pdfUrl || !request) return
    // Android/iOS: the download attribute on a blob URL is unreliable; a new tab works everywhere.
    if (isMobile) { openPdf(); return }
    const a = document.createElement('a')
    a.href = pdfUrl
    a.download = `${(request.title || 'document').replace(/[^\w.-]+/g, '_').slice(0, 80)}${request.stored ? '-signed' : ''}.pdf`
    a.click()
  }

  const title = request?.title || t('signing.defaultTitle')
  const signerName = request ? `${request.signer.firstName} ${request.signer.lastName}` : ''
  const pagesLabel = request ? request.signaturePages.join(', ') : ''
  const shownDestination = destination || request?.destinationMasked || ''
  const viaSms = request?.authentication === 'sms_otp'

  return (
    <div className="min-h-screen bg-muted flex flex-col">
      <main className="flex-1 flex items-start justify-center p-4 py-8">
        <div className="bg-white rounded-card shadow-sm p-6 sm:p-8 max-w-2xl w-full">
          <div className="flex justify-center mb-6"><Logo size={100} /></div>

          {request?.sandbox && (
            <div className="mb-4 rounded-card border border-amber-300 bg-amber-50 text-amber-900 text-small px-3 py-2 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{t('signing.sandboxNotice')}</span>
            </div>
          )}

          {phase === 'loading' && (
            <div className="text-center py-8 text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> {t('common.loading')}
            </div>
          )}

          {(phase === 'invalid' || phase === 'expired' || phase === 'canceled') && (
            <div className="text-center py-4">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h1 className="text-h2 text-foreground mb-2">{t(`signing.${phase}.title`)}</h1>
              <p className="text-body text-muted-foreground">{t(`signing.${phase}.body`)}</p>
              {tenant?.contactEmail && (
                <p className="text-small text-muted-foreground mt-4">
                  <a className="underline" href={`mailto:${tenant.contactEmail}`}>{tenant.contactEmail}</a>
                </p>
              )}
            </div>
          )}

          {(phase === 'read' || phase === 'code') && request && (
            <>
              <h1 className="text-h2 text-foreground mb-1 text-center">{title}</h1>
              <p className="text-body text-muted-foreground mb-5 text-center">{t('signing.greeting', { name: signerName })}</p>

              {/* The document — it must be fully reviewable before signing */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-small font-medium text-foreground flex items-center gap-1.5">
                    <FileText className="w-4 h-4" />{t('signing.document')} <span className="text-muted-foreground font-normal">· {t('signing.pages', { count: request.pages })}</span>
                  </span>
                  {pdfUrl && (
                    <button type="button" onClick={openPdf} className="text-small text-primary underline flex items-center gap-1">
                      <ExternalLink className="w-3.5 h-3.5" /> {t('common.openInNewTab')}
                    </button>
                  )}
                </div>
                {pdfUrl && isMobile ? (
                  <div className="border border-border rounded-card p-4 bg-muted/40 flex flex-col items-center gap-3 text-center">
                    <FileText className="w-8 h-8 text-primary" />
                    <Button type="button" variant="secondary" onClick={openPdf} className="w-full sm:w-auto inline-flex items-center justify-center gap-2">
                      <ExternalLink className="w-4 h-4" /> {t('common.openDocument')}
                    </Button>
                  </div>
                ) : pdfUrl ? (
                  <iframe title={t('signing.document')} src={`${pdfUrl}#navpanes=0&toolbar=0&view=FitH&pagemode=none`} className="w-full h-[460px] sm:h-[620px] border border-border rounded-card bg-white" />
                ) : (
                  <div className="h-40 border border-dashed border-border rounded-card flex items-center justify-center text-small text-muted-foreground">
                    {pdfLoading ? t('common.loading') : t('common.previewUnavailable')}
                  </div>
                )}
              </div>

              {/* What will be placed on the document */}
              <div className="mb-5 bg-muted/50 rounded-card p-4">
                {request.hasSignatureImage ? (
                  <>
                    <p className="text-small font-medium text-foreground mb-2">{t('signing.signatureTitle')}</p>
                    <div className="bg-white border border-border rounded-card h-24 flex items-center justify-center overflow-hidden">
                      {signatureUrl
                        ? <img src={signatureUrl} alt={t('signing.signatureTitle')} className="max-h-20 max-w-full object-contain" />
                        : <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                    </div>
                    <p className="text-small text-muted-foreground mt-2">{t('signing.signatureHint', { pages: pagesLabel })}</p>
                  </>
                ) : (
                  <p className="text-small text-muted-foreground">{t('signing.signatureNameHint', { pages: pagesLabel })}</p>
                )}
              </div>

              {phase === 'read' && (
                <div className="space-y-4">
                  <label className="flex items-start gap-3 text-small text-foreground cursor-pointer font-medium">
                    <input type="checkbox" className="mt-0.5" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
                    <span>{t('signing.agree')} <span className="text-destructive">*</span></span>
                  </label>

                  {request.authentication === 'none' ? (
                    <>
                      <Button onClick={() => void sign()} disabled={!agreed || busy} className="w-full">
                        {busy ? t('signing.signingNow') : t('signing.signNow')}
                      </Button>
                      <p className="text-small text-muted-foreground flex items-start gap-2">
                        <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{t('signing.noneHint')}</span>
                      </p>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => void sendCode(false)} disabled={!agreed || busy} className="w-full">
                        {busy ? t('common.sending') : t(viaSms ? 'signing.sendCodeSms' : 'signing.sendCodeEmail')}
                      </Button>
                      <p className="text-small text-muted-foreground flex items-start gap-2">
                        <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{t(viaSms ? 'signing.codeHintSms' : 'signing.codeHintEmail', { destination: shownDestination })}</span>
                      </p>
                      <p className="text-small text-muted-foreground">{t('signing.wrongDestination')}</p>
                    </>
                  )}
                </div>
              )}

              {phase === 'code' && (
                <form onSubmit={e => { e.preventDefault(); void sign() }} className="space-y-4">
                  <p className="text-body text-foreground text-center">{t('signing.enterCode', { destination: shownDestination })}</p>
                  <div className="flex justify-center gap-2" onPaste={onOtpPaste}>
                    {code.map((d, i) => (
                      <input
                        key={i}
                        ref={el => { otpRefs.current[i] = el }}
                        inputMode="numeric"
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        maxLength={1}
                        value={d}
                        onChange={e => onOtpChange(i, e.target.value)}
                        onKeyDown={e => onOtpKeyDown(i, e)}
                        className="w-11 h-12 text-center text-lg border border-border rounded-input focus:outline-none focus:ring-2 focus:ring-primary"
                        aria-label={`${t('signing.digit')} ${i + 1}`}
                      />
                    ))}
                  </div>
                  <Button type="submit" disabled={code.join('').length !== OTP_LENGTH || busy} className="w-full">
                    {busy ? t('signing.signingNow') : t('signing.signNow')}
                  </Button>
                  <p className="text-small text-muted-foreground text-center">
                    {t('step10.noCodeReceived')}{' '}
                    <button type="button" disabled={cooldown > 0 || busy} onClick={() => void sendCode(true)} className="underline disabled:no-underline disabled:opacity-60">
                      {cooldown > 0 ? t('step10.resendIn', { s: cooldown }) : t('step10.resend')}
                    </button>
                  </p>
                </form>
              )}
            </>
          )}

          {phase === 'signed' && request && (
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h1 className="text-h2 text-foreground mb-2">{t('signing.signed.title')}</h1>
              <p className="text-body text-muted-foreground mb-6">
                {request.stored ? t('signing.signed.stored') : t('signing.signed.preparing')}
              </p>
              {request.stored && pdfUrl && signedPdfReady ? (
                <Button variant="download" onClick={download} className="w-full sm:w-auto inline-flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" /> {t('signing.signed.download')}
                </Button>
              ) : copyFollowsLater ? (
                <p className="text-small text-muted-foreground">{t('signing.signed.later')}</p>
              ) : (
                <div className="text-small text-muted-foreground flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading')}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <LegalFooter />
    </div>
  )
}
